import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';

import { Store } from '@ngrx/store';

import { combineLatest, Observable, Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { v4 as uuidv4 } from 'uuid';

import { removeWhitespaces } from 'src/app/core/custom-form-validators';
import { Language, LanguageCode } from 'src/app/models/language';
import { LANGUAGES } from 'src/app/core/dashboard-constants';
import { UploadIds, UploadKeys } from 'src/app/models/upload';
import { AppState } from 'src/app/state';
import {
  selectIsLoadingTemplate,
  selectSelectedLanguage,
  selectSelectedTemplate,
  selectSupportedLanguages,
  selectTemplateLoaded,
} from 'src/app/dashboard/state/templates/templates.selectors';
import { setLanguageTabIndex } from 'src/app/core/languages-helper';
import {
  addTemplateSupportedLanguages,
  createTemplate,
  setSelectedLanguage,
  setTemplateAsReady,
  updateTemplate,
} from 'src/app/dashboard/state/templates/templates.actions';
import { Intervention } from 'src/app/models/intervention';
import { Template } from 'src/app/models/template';
import { isThereEmptyTranslation } from '../../../helpers/translation-helper';
import { selectInterventions } from 'src/app/dashboard/state/intervention/intervention.selectors';
import { uploadReset } from 'src/app/dashboard/state/upload/upload.actions';
import { selectSelectedStudy } from 'src/app/dashboard/state/study/study.selectors';
import { Study } from '../../../../../models/study';

const TEMPLATE_TITLE_IDENTIFIER = 'title-main';

@Component({
  selector: 'dashboard-template-form',
  templateUrl: './template-form.component.html',
  styleUrls: ['./template-form.component.scss'],
})
export class TemplateFormComponent implements OnInit, OnDestroy {
  @Input() isEdit: boolean;
  @Input() availableInterventions: Intervention[];
  @ViewChild('languageTabs', { static: false }) languageTabs: MatTabGroup;

  templateForm: FormGroup;

  loadingTemplate$: Observable<boolean>;
  loadingIntervention$: Observable<boolean>;
  templateLoaded$: Observable<boolean>;
  selectedTemplate: Template;

  imgPath: string;
  isImageToEdit: boolean;
  idsForUpload: UploadIds;
  keyForUpload = UploadKeys.UPLOAD_FROM_TEMPLATE;

  supportedLanguages: Language[];
  selectedLanguage: Language;
  selectedLanguageDropdown: Language;
  languagesArray = LANGUAGES;

  selectedStudy: Study;

  chosenIntervention: Intervention;

  imageUploadUuid: string;

  private subscriptions = new Subscription();

  constructor(private formBuilder: FormBuilder, private store: Store<AppState>, private toastrService: ToastrService) {}

  ngOnInit(): void {
    this.templateForm = this.formBuilder.group({
      translations: this.formBuilder.array([]),
      templateName: ['', Validators.required],
      imageUrl: [null],
    });

    this.imageUploadUuid = uuidv4();

    this.subscriptions.add(
      this.store.select(selectSupportedLanguages).subscribe((languages) => (this.supportedLanguages = languages))
    );
    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((language) => (this.selectedLanguage = language))
    );

    this.subscriptions.add(
      this.store.select(selectSelectedStudy).subscribe((study) => {
        this.selectedStudy = study;
        this.idsForUpload = {
          studyId: study?.id,
        };
      })
    );

    this.loadingTemplate$ = this.store.select(selectIsLoadingTemplate);
    this.templateLoaded$ = this.store.select(selectTemplateLoaded);

    this.subscriptions.add(
      combineLatest([this.store.select(selectInterventions), this.store.select(selectSelectedTemplate)]).subscribe(
        ([interventions, template]) => {
          this.selectedTemplate = template;
          if (template) {
            this.isEdit = true;
            const templateIntervention = interventions?.find(
              (intervention) => intervention.id === template.interventionId
            );
            this.chosenIntervention = templateIntervention;
            this.removeAllTranslationFields();
            this.selectedTemplate.languages?.forEach((langCode) => this.createTranslationFields(langCode));
            if (this.selectedTemplate?.imageUrl) {
              this.isImageToEdit = true;
              this.imgPath = this.selectedTemplate.imageUrl;
            } else {
              this.isImageToEdit = false;
              this.imgPath = null;
              this.store.dispatch(uploadReset({ uuid: this.imageUploadUuid }));
            }
          } else {
            this.templateForm.reset();
            this.store.dispatch(uploadReset({ uuid: this.imageUploadUuid }));
            this.removeAllTranslationFields();
            this.createTranslationFields(LanguageCode.ENGLISH);
          }
        }
      )
    );
  }

  get translations(): FormArray {
    return this.templateForm.get('translations') as FormArray;
  }

  get templateName(): AbstractControl {
    return this.templateForm.get('templateName') as AbstractControl;
  }

  get imageUrl(): AbstractControl {
    return this.templateForm.get('imageUrl') as AbstractControl;
  }

  selectLanguage(chosenLanguage: Language): void {
    this.selectedLanguageDropdown = chosenLanguage;
  }

  addLanguage(): void {
    if (
      this.selectedLanguageDropdown &&
      !this.supportedLanguages.find((lang) => lang.code === this.selectedLanguageDropdown.code)
    ) {
      const newSupportedLanguages = setLanguageTabIndex([...this.supportedLanguages, this.selectedLanguageDropdown]);
      this.store.dispatch(
        addTemplateSupportedLanguages({
          supportedLanguages: newSupportedLanguages,
        })
      );

      this.createTranslationFields(this.selectedLanguageDropdown.code);

      this.store.dispatch(
        setSelectedLanguage({
          selectedLanguage: {
            ...this.selectedLanguageDropdown,
            tabIndex: this.supportedLanguages.length,
          },
        })
      );
    }
  }

  setTemplateAsReady(event: any): void {
    const languageCodes = [];
    this.supportedLanguages.map((lang) => languageCodes.push(lang.code));
    const templateDetails = {
      ...this.templateForm.value,
      studyId: this.chosenIntervention?.studyId,
      interventionId: this.chosenIntervention?.id,
      languages: languageCodes.toString(),
      id: this.selectedTemplate?.id,
      sections: this.selectedTemplate?.sections,
      feedbackTemplateReady: event.checked,
    };
    this.store.dispatch(setTemplateAsReady({ template: templateDetails }));
  }

  removeLanguage(selectedLanguage: Language): void {
    const newSupportedLanguages = [...this.supportedLanguages];

    if (newSupportedLanguages.length <= 1) {
      this.toastrService.error('There should be at least one language selected');
      return;
    }

    const indexToRemove = newSupportedLanguages.indexOf(selectedLanguage);
    if (indexToRemove >= 0) {
      newSupportedLanguages.splice(indexToRemove, 1);
    }

    const indexedLanguages = setLanguageTabIndex(newSupportedLanguages);
    this.store.dispatch(addTemplateSupportedLanguages({ supportedLanguages: indexedLanguages }));

    if (selectedLanguage.code === this.selectedLanguage.code) {
      this.store.dispatch(setSelectedLanguage({ selectedLanguage: this.supportedLanguages[0] }));
    }

    this.translations.removeAt(
      this.translations.value.findIndex((translation) => translation.language === selectedLanguage.code)
    );
  }

  createTranslationFields(languageCode): void {
    const filteredTranslations = this.selectedTemplate?.translations?.filter(
      (translation) => translation.language === languageCode
    );

    const templateTitle = this.formBuilder.group({
      id: [filteredTranslations ? filteredTranslations[0]?.id : 0],
      language: [languageCode],
      identifier: [TEMPLATE_TITLE_IDENTIFIER],
      translation: [
        filteredTranslations ? filteredTranslations[0]?.translation : '',
        [Validators.required, removeWhitespaces],
      ],
    });

    this.translations.push(templateTitle);
  }

  removeAllTranslationFields(): void {
    this.translations.clear();
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language.name === event.tab.textLabel);
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
  }

  selectFirstTab() {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
  }

  selectIntervention(intervention: Intervention) {
    this.chosenIntervention = intervention;
  }

  saveTemplate() {
    this.templateForm.markAllAsTouched();

    if (isThereEmptyTranslation(this.translations.value, TEMPLATE_TITLE_IDENTIFIER)) {
      this.toastrService.error('Please populate Title in all selected languages');
      return;
    }

    if (this.templateForm.invalid) {
      return;
    }

    const languageCodes = [];
    this.supportedLanguages.map((lang) => languageCodes.push(lang.code));

    let templateDetails = {
      ...this.templateForm.value,
      studyId: this.selectedStudy?.id,
      interventionId: this.chosenIntervention?.id,
      languages: languageCodes.toString(),
      sections: this.selectedTemplate?.sections || [],
      feedbackTemplateReady: this.selectedTemplate?.feedbackTemplateReady,
    };

    if (this.chosenIntervention) {
      this.availableInterventions.splice(this.availableInterventions.indexOf(this.chosenIntervention), 1);
    }

    if (this.isEdit) {
      templateDetails = {
        ...templateDetails,
        id: this.selectedTemplate?.id,
      };
      this.store.dispatch(updateTemplate({ template: templateDetails }));
    } else {
      this.store.dispatch(createTemplate({ template: templateDetails }));
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
